// noinspection JSUnusedGlobalSymbols,JSUnresolvedReference

import Storage from '../helpers/Storage';
import Text from '../helpers/Text';

export class Product {
    constructor({
        parent,
        id,
        sku,
        ean,
        name,
        brand,
        description,
        longDescription,
        isFeatured,
        featuredData,
        categories,
        categoryPaths,
        multimedia,
        createdAt,
        updatedAt,
        originUpdatedAt,
        normalPrice,
        offerPrice,
        exclusivePrice,
        sellerName,
        externalSale,
        externalSaleUrl,
        clientDelivery,
        internationalShipping,
        onRequest,
        maxPerItem,
        saleUnit,
        has3dModel,
        viewItPreviewUrl,
        viewItSceneUrl,
        viewItArUrl,
        delivery,
        freeDelivery,
        pickup,
        fastPickup,
        fastDelivery,
        features,
        measures,
        variants,
        variantsMatches,
        order,
        score,
        rating,
        reviewCount,
        tags,
        paymentOptions,
        upsell,
        crossSell
    }) {
        this.parent = parent;
        this.id = id;
        this.sku = sku;
        this.ean = ean;
        this.name = name;
        this.brand = brand;
        this.description = description;
        this.longDescription = longDescription;
        this.isFeatured = isFeatured;
        this.featuredData = featuredData;
        this.categories = categories;
        this.categoryPaths = categoryPaths;
        this.multimedia = multimedia;
        this.createdAt = createdAt;
        this.updatedAt = updatedAt;
        this.originUpdatedAt = originUpdatedAt;
        this.normalPrice = normalPrice;
        this.offerPrice = offerPrice;
        this.exclusivePrice = exclusivePrice;
        this.sellerName = sellerName;
        this.externalSale = externalSale;
        this.externalSaleUrl = externalSaleUrl;
        this.clientDelivery = clientDelivery;
        this.internationalShipping = internationalShipping;
        this.onRequest = onRequest;
        this.maxPerItem = maxPerItem || 0;
        this.saleUnit = saleUnit;
        this.has3dModel = has3dModel;
        this.viewItPreviewUrl = viewItPreviewUrl;
        this.viewItSceneUrl = viewItSceneUrl;
        this.viewItArUrl = viewItArUrl;
        this.delivery = delivery;
        this.freeDelivery = freeDelivery;
        this.pickup = pickup;
        this.fastPickup = fastPickup;
        this.fastDelivery = fastDelivery;
        this.features = features;
        this.measures = measures;
        this.variants = variants;
        this.variantsMatches = variantsMatches;
        this.order = order;
        this.score = score;
        this.rating = rating;
        this.reviewCount = reviewCount || 0;
        this.tags = tags || [];
        this.paymentOptions = paymentOptions || [];
        this.upsell = upsell || [];
        this.crossSell = crossSell || [];
    };

    getMainImage(size) {
        return this.multimedia && this.multimedia.mainImage && this.multimedia.mainImage[size] ? this.multimedia.mainImage[size] : undefined;
    };

    getMainCategory() {
        let categoryId;
        try {
            categoryId = this.categoryPaths && this.categoryPaths.length > 0 ? this.categoryPaths[0][1] : undefined;
        } catch (error) {
            console.error(error);
            categoryId = undefined;
        }
        return categoryId;
    };

    inCatalog(catalog) {
        const categories = catalog ? catalog.categories : [];        
        let inCatalog = false;
        this.categories.forEach((category) => {
            const exists = !!categories.find((c) => c === category);
            if (exists) inCatalog = true;
        });
        return inCatalog;
    };

    getOfferDiscountPercentage() {
        return Math.round((this.normalPrice || 0) > 0 && this.offerPrice ? 100 - (((this.offerPrice || 0) * 100) / this.normalPrice) : 0);
    };

    getExclusiveDiscountPercentage() {
        return Math.round((this.normalPrice || 0) > 0 && this.exclusivePrice ? 100 - (((this.exclusivePrice || 0) * 100) / this.normalPrice) : 0);
    };

    validFeatures(validateVisibility, validateIndexability, showAIGenerated) {
        const validFeatures = this.features.filter((feature) => {
            const validVisibility = validateVisibility ? feature.visible : true;
            const validIndexability = validateIndexability ? feature.indexable : true;
            const validSource = (feature.aiGenerated && showAIGenerated) || !feature.aiGenerated;
            return validVisibility && validIndexability && validSource;
        });
        const sortedFeatures = validFeatures.sort((a, b) => {
            const aOrder = a.order || Number.MAX_SAFE_INTEGER;
            const bOrder = b.order || Number.MAX_SAFE_INTEGER;
            return aOrder - bOrder;
        });
        return sortedFeatures;
    };

    indexableFeatures() {
        return this.features.filter(feature => feature.indexable);
    };

    visibleFeatures() {
        return this.features.filter(feature => feature.visible);
    };

    getSortedFeatures() {
        return this.visibleFeatures().sort((a, b) => a.order - b.order);
    };

    minPrice() {
        return this.exclusivePrice || this.offerPrice || this.normalPrice;
    };

    maxPrice() {
        return this.normalPrice || this.offerPrice || this.exclusivePrice;
    };

    maxDiscount() {
        const offerDiscount = this.getOfferDiscountPercentage();
        const exclusiveDiscount = this.getExclusiveDiscountPercentage();
        return Math.max(offerDiscount, exclusiveDiscount);
    };

    hasStock() {
        if (this.variants && this.variants.length > 0) {
            return this.maxPerItem > 0 && this.variants.some(variant => variant.maxPerItem > 0);
        } else {
            return this.maxPerItem > 0;
        }
    };

    hasAnyPrice() {
        return !!this.normalPrice || !!this.offerPrice || !!this.exclusivePrice;
    };

    isValid() {
        return this.hasStock() && this.hasAnyPrice();
    };

    size() {
        let size = {width: undefined, height: undefined, depth: undefined, unit: undefined};
        if (this.measures && this.measures.length > 0) {
            const widthMeasure  = this.measures.find(measure => measure.name === 'width');
            const heightMeasure = this.measures.find(measure => measure.name === 'height');
            const depthMeasure  = this.measures.find(measure => measure.name === 'length');
            if (widthMeasure)  {
                size.width = widthMeasure.value;
                if (!size.unit) size.unit = widthMeasure.unit;
            }
            if (heightMeasure) {
                size.height = heightMeasure.value;
                if (!size.unit) size.unit = heightMeasure.unit;
            }
            if (depthMeasure) {
                size.depth = depthMeasure.value;
                if (!size.unit) size.unit = depthMeasure.unit;
            }
        }
        return size;
    };

    static filterByCategories = (products, categories) => {
        if (categories.length > 0) {
            return products.filter(product => {
                const productCategories = product.categories;
                const match = productCategories.find(productCategory => categories.includes(productCategory));
                return match !== undefined;
            });
        } else return [];
    };

    static filterByFilters = (products, filters, showExternalSale, externalSaleLabel, clientName, showAIGenerated) => {
        const resultsByFilter = [];
        if (filters.length > 0) {
            filters.forEach((filter) => {
                let results = [];
                if (filter.type === 'brands') results = this.filterByBrands(products, filter);
                if (filter.type === 'size')   results = this.filterBySize(products, filter);
                if (filter.type === 'price')  results = this.filterByPrice(products, filter);
                if (filter.type === 'external-sale' && showExternalSale) results = this.filterByExternalSale(products, filter, externalSaleLabel, clientName);
                if (filter.type === 'normal') results = this.filterByFeatures(products, filter, showAIGenerated);
                resultsByFilter.push({id: filter.id, label: filter.label, type: filter.type, results: results});
            });
        }
        let results = [...products];
        resultsByFilter.forEach((byFilter) => {
            results = results.filter(product => byFilter.results.includes(product));
        });
        return results;
    };

    static filterByBrands = (products, filter) => {
        const brandsValues = filter.options.map(option => Text.minify(option.label));
        return products.filter(product => {
            const brand = Text.minify(product.brand);
            return brandsValues.includes(brand);
        });
    };

    static filterBySize = (products, filter) => {
        let results = products;
        ['width-range', 'height-range', 'depth-range'].forEach(id => {
            const option = filter.options.find(option => option.id === id);
            const min = option ? option.min : undefined;
            const value = option ? option.value : undefined;
            if (min && value) {
                const inRange = products.filter(product => {
                    const type = id.split('-')[0];
                    const size = product.size();
                    const measure = size[type];
                    return measure >= min && measure <= value;
                });
                results = results.filter(product => inRange.includes(product));
            }
        });
        return results;
    };

    static filterByPrice = (products, filter) => {
        const option = filter.options[0];
        const startValue = option && option.startValue ? option.startValue : undefined;
        const endValue = option && option.endValue ? option.endValue : undefined;
        if (startValue === undefined || endValue === undefined) return [];
        return products.filter(product => {
            const minPrice = product.minPrice();
            const maxPrice = product.maxPrice();
            const minPriceInRange = minPrice >= startValue && minPrice <= endValue;
            const maxPriceInRange = maxPrice >= startValue && maxPrice <= endValue;
            return minPriceInRange || maxPriceInRange;
        });
    };

    static filterByExternalSale = (products, filter, externalSaleLabel, clientName) => {
        const externalSaleValues = filter.options.map(option => Text.minify(option.label));
        return products.filter(product => {
            const externalSale = Text.minify(product.externalSale ? externalSaleLabel : clientName);
            return externalSaleValues.includes(externalSale);
        });
    };

    static filterByFeatures = (products, filter, showAIGenerated) => {
        const valuesMap = filter.options.map(option => ({name: filter.id, value: option.id}));
        return products.filter(product => {
            return !!product.validFeatures(true, true, showAIGenerated).find(feature => {
                const featureNameHash = Text.toHash(feature.name);
                const featureValueHash = Text.toHash(feature.value);
                return !!valuesMap.find(valuesMapItem => valuesMapItem.name === featureNameHash && valuesMapItem.value === featureValueHash);
            });
        });
    };

    static filterValidProducts = (products) => {
        return products && products.length > 0 ? (products.filter(product => product.isValid())) : [];
    };

    static filterByExclusivePrice = (products) => {
        return products.filter((product) => !!product.exclusivePrice);
    };

    static filterByDiscounts = (products) => {
        return products.filter((product) => {
            const offerDiscount = product.getOfferDiscountPercentage();
            const exclusiveDiscount = product.getExclusiveDiscountPercentage();
            return offerDiscount > 0 || exclusiveDiscount > 0;
        });
    };

    static filterByFreeDelivery = (products) => {
        return products.filter((product) => !!product.freeDelivery);
    };

    static filterByPickup = (products) => {
        return products.filter((product) => !!product.pickup);
    };

    static sortByOrder = (products) => {
        return products.sort((p1, p2) => {
            const order1 = p1.order ?? Number.MAX_SAFE_INTEGER;
            const order2 = p2.order ?? Number.MAX_SAFE_INTEGER;
            if (order1 !== order2) return order1 - order2;
            const name1 = p1.name?.toLowerCase() ?? "";
            const name2 = p2.name?.toLowerCase() ?? "";
            return name1.localeCompare(name2);
        });
    };

    static sortByHighestPrice = (products) => {
        return products.sort((p1, p2) => p2.minPrice() - p1.minPrice());
    };

    static sortByLowestPrice = (products) => {
        return products.sort((p1, p2) => p1.minPrice() - p2.minPrice());
    };

    static sortByFeatured = (products) => {
        return Product.sortByOrder(products);
    };

    static sortBy3dModel = (products) => {
        let with3dModel = products.filter(product => product.has3dModel);
        with3dModel = Product.sortByHighestPrice(with3dModel);
        let without3dModel = products.filter(product => !product.has3dModel);
        without3dModel = Product.sortByHighestPrice(without3dModel);
        return with3dModel.concat(without3dModel);
    };

    static sortByDelivery = (products) => {
        let with3dModel = products.filter(product => product.delivery);
        with3dModel = Product.sortByHighestPrice(with3dModel);
        let without3dModel = products.filter(product => !product.delivery);
        without3dModel = Product.sortByHighestPrice(without3dModel);
        return with3dModel.concat(without3dModel);
    };

    static sortByFreeDelivery = (products) => {
        let with3dModel = products.filter(product => product.freeDelivery);
        with3dModel = Product.sortByHighestPrice(with3dModel);
        let without3dModel = products.filter(product => !product.freeDelivery);
        without3dModel = Product.sortByHighestPrice(without3dModel);
        return with3dModel.concat(without3dModel);
    };

    static sortByPickup = (products) => {
        let with3dModel = products.filter(product => product.pickup);
        with3dModel = Product.sortByHighestPrice(with3dModel);
        let without3dModel = products.filter(product => !product.pickup);
        without3dModel = Product.sortByHighestPrice(without3dModel);
        return with3dModel.concat(without3dModel);
    };

    static sortByRelevance = (products, searchSorting) => {
        let unsortedProducts = [];
        products.forEach((product) => {
            const index = searchSorting.findIndex((productId) => productId === product.id);
            unsortedProducts.push({index: index, product: product});
        });
        const sortedProducts = unsortedProducts.sort((a, b) => a.index - b.index);
        return sortedProducts.map((item) => item.product);
    };

    static getDatabaseDeliveryOptions = (pricesData, deliveryData) => {
        let deliveryOptions = [];
        const storeId = Storage.getStore();

        // Check first if there is any data in pricesData for the instance store.
        if (storeId && pricesData[storeId]) {
            const storeData = pricesData[storeId];
            deliveryOptions = storeData.delivery_options && storeData.delivery_options.length > 0 ? storeData.delivery_options : [];
        }
        
        // If no data was found for the instance store, check backup stores.
        if (!deliveryOptions || deliveryOptions.length === 0) {
            const backupStores = Storage.getBackupStores();
            for (let i = 0; i < backupStores.length; i++) {
                const backupStoreId = backupStores[i];
                if (pricesData[backupStoreId]) {
                    const storeData = pricesData[backupStoreId];
                    if (!deliveryOptions || deliveryOptions.length === 0) deliveryOptions = storeData.delivery_options && storeData.delivery_options.length > 0 ? storeData.delivery_options : [];
                }
            }
        }

        // If still no data was found, use the deliveryOptions attribute at the product level.
        if (!deliveryOptions || deliveryOptions.length === 0) deliveryOptions = deliveryData && deliveryData.length > 0 ? deliveryData : [];
        return deliveryOptions;
    };

    static getDatabaseMainPriceGroup = (pricesData) => {
        const storeId = Storage.getStore();
        let response = {normal: undefined, offer: undefined, exclusive: undefined};
        if (!pricesData) return response;
        if (storeId && pricesData[storeId]) {
            const prices = pricesData[storeId].prices;
            response.normal    = prices.find(price => price.type === 'normal'   );
            response.offer     = prices.find(price => price.type === 'sale'     );
            response.exclusive = prices.find(price => price.type === 'exclusive');
        } else {
            const backupStores = Storage.getBackupStores();
            for (let i = 0; i < backupStores.length; i++) {
                const backupStoreId = backupStores[i];
                if (pricesData[backupStoreId]) {
                    const prices = pricesData[backupStoreId].prices;
                    response.normal    = prices.find(price => price.type === 'normal'   );
                    response.offer     = prices.find(price => price.type === 'sale'     );
                    response.exclusive = prices.find(price => price.type === 'exclusive');
                    break;
                }
            }
        }
        return response;
    };

    static getDatabaseStoreMaxPerItem = (stockData) => {
        const storeId = Storage.getStore();
        let maxPerItem = 0;
        if (!stockData) return maxPerItem;
        if (!!storeId && !!stockData && !!stockData[storeId]) {
            maxPerItem = stockData[storeId].max_per_item || 0;
        } else {
            const backupStores = Storage.getBackupStores();
            for (let i = 0; i < backupStores.length; i++) {
                const backupStoreId = backupStores[i];
                if (stockData[backupStoreId]) {
                    maxPerItem = stockData[backupStoreId].max_per_item || 0;
                }
            }
        }
        return maxPerItem;
    };

    static getDatabaseStoreOnRequest = (onRequestData) => {
        const storeId = Storage.getStore();
        let onRequest = false;
        if (!onRequestData) return false;
        if (!!storeId && !!onRequestData && !!onRequestData[storeId]) {
            onRequest = onRequestData[storeId].is_custom_price || false;
        } else {
            const backupStores = Storage.getBackupStores();
            for (let i = 0; i < backupStores.length; i++) {
                const backupStoreId = backupStores[i];
                if (onRequestData[backupStoreId]) {
                    onRequest = onRequestData[backupStoreId].is_custom_price || false;
                }
            }
        }
        return onRequest;
    };

    static getDatabaseSaleExtensions = (saleExtensionsData) => {
        if (!saleExtensionsData || saleExtensionsData.length === 0) return [];
        return saleExtensionsData
            .filter((item) => item && item.product_id)
            .map((item) => ({productId: item.product_id, priority: item.priority ?? Number.MAX_SAFE_INTEGER}))
            .sort((a, b) => a.priority - b.priority);
    };

    static getDatabaseOrderByCategory = (orderByCategoryData) => {
        const url = new URL(location.href);
        if (!url) return Number.MAX_SAFE_INTEGER;
        const pathname = url.pathname;
        if (!pathname) return Number.MAX_SAFE_INTEGER;
        const split = pathname.split('/');
        let page = undefined;
        let categoryId = undefined;
        if (split && split.length > 1) {
            page = split[1];
            categoryId = split[2];
        }
        if (page !== 'category') return Number.MAX_SAFE_INTEGER;
        if (!categoryId) return Number.MAX_SAFE_INTEGER;
        let categoryOrder = orderByCategoryData ? orderByCategoryData[categoryId] : undefined;
        categoryOrder = Number.isInteger(categoryOrder) ? categoryOrder : Number.MAX_SAFE_INTEGER;
        return categoryOrder;
    };

    static getUnitText = (unit, plural) => {
        switch (unit) {
            case 'u':
                return plural ? 'unidad(es)' : 'unidad';
            case 'kg':
                return plural ? 'kilogramo(s)' : "kilogramo";
            case 'm':
                return plural ? 'metro(s)' : "metro";
            case 'caja':
                return plural ? 'caja(s)' : 'caja';
            case 'rol':
                return plural ? 'rollo(s)' : 'rollo';
            case 'gl':
                return plural ? 'galón(es)' : 'galón';
            default:
                return unit;
        }
    };

    clone() {
        return new Product({
            parent: this.parent,
            id: this.id,
            sku: this.sku,
            ean: this.ean,
            name: this.name,
            brand: this.brand,
            description: this.description,
            longDescription: this.longDescription,
            isFeatured: this.isFeatured,
            featuredData: this.featuredData ? this.featuredData.clone() : undefined,
            categories: this.categories,
            categoryPaths: this.categoryPaths,
            multimedia: this.multimedia ? this.multimedia.clone() : undefined,
            createdAt: this.createdAt,
            updatedAt: this.updatedAt,
            originUpdatedAt: this.originUpdatedAt,
            normalPrice: this.normalPrice,
            offerPrice: this.offerPrice,
            exclusivePrice: this.exclusivePrice,
            sellerName: this.sellerName,
            externalSale: this.externalSale,
            externalSaleUrl: this.externalSaleUrl,
            clientDelivery: this.clientDelivery,
            internationalShipping: this.internationalShipping,
            onRequest: this.onRequest,
            maxPerItem: this.maxPerItem,
            saleUnit: this.saleUnit,
            has3dModel: this.has3dModel,
            viewItSceneUrl: this.viewItSceneUrl,
            viewItArUrl: this.viewItArUrl,
            viewItPreviewUrl: this.viewItPreviewUrl,
            delivery: this.delivery,
            freeDelivery: this.freeDelivery,
            pickup: this.pickup,
            fastPickup: this.fastPickup,
            fastDelivery: this.fastDelivery,
            features: this.features && this.features.length > 0 ? this.features.map(feature => feature.clone()) : [],
            measures: this.measures && this.measures.length > 0 ? this.measures.map(measure => measure.clone()) : [],
            variants: this.variants && this.variants.length > 0 ? this.variants.map(variant => variant.clone()) : [],
            variantsMatches: this.variantsMatches,
            order: this.order,
            score: this.score,
            rating: this.rating,
            reviewCount: this.reviewCount,
            tags: this.tags && this.tags.length > 0 ? this.tags.map(tag => tag.clone()) : [],
            paymentOptions: this.paymentOptions && this.paymentOptions.length > 0 ? this.paymentOptions.map(option => option.clone()) : [],
            upsell: this.upsell.slice(),
            crossSell: this.crossSell.slice()
        });
    };
}

export const productConverter = {
    toFirestore() {
        return null
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        const deliveryOptions = Product.getDatabaseDeliveryOptions(data.info_stores, data.delivery_options);
        const prices = Product.getDatabaseMainPriceGroup(data.info_stores);
        const maxPerItem = Product.getDatabaseStoreMaxPerItem(data.info_stores);
        const onRequest = Product.getDatabaseStoreOnRequest(data.info_stores);
        const order = data.order || Number.MAX_SAFE_INTEGER; // To use the order by category, we can use: const order = Product.getDatabaseOrderByCategory(data.order_by_category);
        const productMultimedia = data.multimedia;
        const productGallery = productMultimedia && productMultimedia.gallery && productMultimedia.gallery.length > 0 ? productMultimedia.gallery.map(item => new MultimediaItem({
            l: item.l || undefined,
            m: item.m || undefined,
            s: item.s || undefined
        })) : [];
        const productMainImage = new MultimediaItem({
            l: productMultimedia && productMultimedia.main_image && productMultimedia.main_image.l ? productMultimedia.main_image.l : undefined,
            m: productMultimedia && productMultimedia.main_image && productMultimedia.main_image.m ? productMultimedia.main_image.m : undefined,
            s: productMultimedia && productMultimedia.main_image && productMultimedia.main_image.s ? productMultimedia.main_image.s : undefined
        });
        return new Product({
            parent: undefined,
            id: snapshot.id,
            sku: data.sku,
            ean: data.ean,
            name: data.name,
            brand: data.brand,
            description: data.description,
            longDescription: data.long_description,
            isFeatured: data.is_featured,
            featuredData: new FeaturedData({
                label: data.featured_data && data.featured_data.label ? data.featured_data.label : undefined,
                icon: data.featured_data && data.featured_data.icon ? data.featured_data.icon : undefined,
                mediaType: data.featured_data && data.featured_data.media_type ? data.featured_data.media_type : undefined,
                mediaWidthPercentage: data.featured_data && data.featured_data.media_width_percentage ? data.featured_data.media_width_percentage : undefined
            }),
            categories: data.categories,
            categoryPaths: data.category_paths,
            multimedia: new Multimedia({gallery: productGallery, mainImage: productMainImage}),
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            originUpdatedAt: data.origin_updated_at,
            normalPrice: prices.normal ? prices.normal.value : undefined,
            offerPrice: prices.offer ? prices.offer.value : undefined,
            exclusivePrice: prices.exclusive ? prices.exclusive.value : undefined,
            sellerName: data.seller_name || undefined,
            externalSale: data.is_external_site_sell || false,
            externalSaleUrl: data.external_site_url || undefined,
            clientDelivery: data.is_client_delivery,
            internationalShipping: data.is_international_shipping,
            onRequest: onRequest,
            maxPerItem: maxPerItem,
            saleUnit: data.unit,
            has3dModel: data.has_3d_model || false,
            viewItSceneUrl: data.has_3d_model && data.services && data.services.view_it ? data.services.view_it.sceneUrl : undefined,
            viewItArUrl: data.has_3d_model && data.services && data.services.view_it ? data.services.view_it.arUrl : undefined,
            viewItPreviewUrl: data.has_3d_model && data.services && data.services.view_it ? data.services.view_it.modelPreview : undefined,
            delivery: deliveryOptions.includes('Shipping'),
            freeDelivery: deliveryOptions.includes('FreeShipping'),
            pickup: deliveryOptions.includes('Pickup'),
            fastPickup: deliveryOptions.includes('FastPickup'),
            fastDelivery: deliveryOptions.includes('FastShipping'),
            features: data.features && data.features.length > 0 ? data.features.map(feature => {
                return new Feature({
                    name: feature.name,
                    value: feature.value,
                    order: feature.order,
                    visible: feature.visibility,
                    indexable: feature.indexable,
                    aiGenerated: feature.ai_generated
                });
            }) : [],
            measures: data.measures && data.measures.length > 0 ? data.measures.map(measure => {
                return new Measure({
                    name: measure.name,
                    value: measure.value,
                    unit: measure.unit
                });
            }) : [],
            variants: data.variants && data.variants.length > 0 && data.variants.map((variant) => {
                const variantMultimedia = variant.multimedia;
                const variantGallery = variantMultimedia && variantMultimedia.gallery && variantMultimedia.gallery.length > 0 ? variantMultimedia.gallery.map(item => new MultimediaItem({
                    l: item.l || undefined,
                    m: item.m || undefined,
                    s: item.s || undefined
                })) : [];
                const variantMainImage = new MultimediaItem({
                    l: variantMultimedia && variantMultimedia.main_image && variantMultimedia.main_image.l ? variantMultimedia.main_image.l : undefined,
                    m: variantMultimedia && variantMultimedia.main_image && variantMultimedia.main_image.m ? variantMultimedia.main_image.m : undefined,
                    s: variantMultimedia && variantMultimedia.main_image && variantMultimedia.main_image.s ? variantMultimedia.main_image.s : undefined
                });
                const variantPrices = Product.getDatabaseMainPriceGroup(variant.info_stores);
                const variantMaxPerItem = Product.getDatabaseStoreMaxPerItem(variant.info_stores);
                const variantOnRequest = Product.getDatabaseStoreOnRequest(variant.info_stores);
                return new Product({
                    id: variant.id,
                    sku: variant.sku,
                    name: variant.name,
                    normalPrice: variantPrices.normal ? variantPrices.normal.value : undefined,
                    offerPrice: variantPrices.offer ? variantPrices.offer.value : undefined,
                    exclusivePrice: variantPrices.exclusive ? variantPrices.exclusive.value : undefined,
                    maxPerItem: variantMaxPerItem,
                    saleUnit: variant.unit,
                    multimedia: new Multimedia({gallery: variantGallery, mainImage: variantMainImage}),
                    onRequest: variantOnRequest
                });
            }),
            order: order,
            score: undefined,
            rating: data.ranking || 0,
            reviewCount: data.review_count || 0,
            tags: data.tags && data.tags.length > 0 ? data.tags.map((tag) => (
                new Tag({key: tag.key, value: tag.value, type: tag.type || 'label'})
            )) : [],
            paymentOptions: data.payment_options && data.payment_options.length > 0 ? data.payment_options.map(option => (
                new PaymentOption({id: option.id, icon: option.icon, label: option.label})
            )) : [],
            upsell: Product.getDatabaseSaleExtensions(data.upsell),
            crossSell: Product.getDatabaseSaleExtensions(data.cross_sell)
        });
    }
};

export class Multimedia {
    constructor({gallery, mainImage}) {
        this.gallery = gallery;
        this.mainImage = mainImage;
    };

    clone() {
        return new Multimedia({
            gallery: this.gallery && this.gallery.length > 0 ? this.gallery.map(item => item.clone()) : [],
            mainImage: this.mainImage ? this.mainImage.clone() : undefined
        });
    };
};

export class MultimediaItem {
    constructor({l, m, s}) {
        this.l = l;
        this.m = m;
        this.s = s;
    };

    clone() {
        return new MultimediaItem({
            l: this.l,
            m: this.m,
            s: this.s
        });
    }
};

export class Feature {
    constructor({name, value, order, visible, indexable, aiGenerated}) {
        this.name = name;
        this.value = value;
        this.order = order;
        this.visible = visible;
        this.indexable = indexable;
        this.aiGenerated = aiGenerated || false;
    };

    clone() {
        return new Feature({
            name: this.name,
            value: this.value,
            order: this.order,
            visible: this.visible,
            indexable: this.indexable,
            aiGenerated: this.aiGenerated
        });
    }
};

export class FeaturedData {
    constructor({icon, label, mediaType, mediaWidthPercentage}) {
        this.icon = icon;
        this.label = label;
        this.mediaType = mediaType;
        this.mediaWidthPercentage = mediaWidthPercentage;
    };

    clone() {
        return new FeaturedData({
            icon: this.icon,
            label: this.label,
            mediaType: this.mediaType,
            mediaWidthPercentage: this.mediaWidthPercentage
        });
    };
};

export class Measure {
    constructor({name, value, unit}) {
        this.name = name;
        this.value = value;
        this.unit = unit;
    };

    clone() {
        return new Measure({
            name: this.name,
            value: this.value,
            unit: this.unit
        });
    };
};

export class Tag {
    constructor({key, value, type, icon, labelColor, backgroundColor, borderColor}) {
        this.key = key;
        this.value = value;
        this.type = type;
        this.icon = icon;
        this.labelColor = labelColor;
        this.backgroundColor = backgroundColor;
        this.borderColor = borderColor;
    };

    clone() {
        return new Tag({
            key: this.key,
            value: this.value,
            type: this.type,
            icon: this.icon,
            labelColor: this.labelColor,
            backgroundColor: this.backgroundColor,
            borderColor: this.borderColor
        });
    };
};

export class PaymentOption {
    constructor({id, icon, label}) {
        this.id = id;
        this.icon = icon;
        this.label = label;
    };

    clone() {
        return new Tag({
            id: this.id,
            icon: this.icon,
            label: this.label
        });
    };
}